import axios from 'axios';
import { cloneDeep, isNil, isObject } from 'lodash';

import { RESET_ALL as RESET_ON_LOGOUT } from 'redux-store/reducers/common-reducer';
import { deletePosts } from 'redux-store/reducers/instagramPostsDeleteReducer';

import { createReducer } from 'utils/redux';
import { API, post } from 'utils/api';
import { getPostsToDelete } from 'utils/posts';

import { getBrandProfileId } from 'utils/organization';
import { DEFAULT_FILTERS } from '../utils/filters';
import {
  DEFAULT_SORT_OPTION,
  ITEMS_PER_PAGE,
  buildQueryObjectFromFilters,
  getPreparedPreloadImages,
  getSearchTerms
} from '../utils/content';

import type {
  Action,
  DiscoveryContentFilterDetails,
  DiscoveryContentFilters,
  DiscoveryContentResultImage,
  DiscoveryContentState,
  DiscoveryFilterOptions,
  DiscoveryScreenState,
  Dispatch,
  GetState,
  PaginationLinks,
  PaginationMeta,
  ThunkAction
} from 'types';

const { DISCOVERY } = API.END_POINTS;

const CONTENT_LOADING = 'discovery/content/CONTENT_LOADING';
const CONTENT_LOADING_MORE = 'discovery/content/CONTENT_LOADING_MORE';
const CONTENT_LOADED = 'discovery/content/CONTENT_LOADED';
const CONTENT_RESET = 'discovery/content/CONTENT_RESET';
const FILTERS_UPDATED = 'discovery/content/FILTERS_UPDATED';
const FILTERS_RESET = 'discovery/content/FILTERS_RESET';
const PAGINATION_UPDATED = 'discovery/content/PAGINATION_UPDATED';
const SCREEN_STATE_UPDATED = 'discovery/content/SCREEN_STATE';

const initialScreenState = {
  page: 0,
  sort: DEFAULT_SORT_OPTION,
  totalItems: 0,
  links: {},
  meta: {
    currentPage: 0,
    itemsCurrentPage: 0,
    itemsPerPage: ITEMS_PER_PAGE,
    itemsTotal: 0,
    pagesTotal: 0
  }
};

const initialState = {
  defaultFilters: DEFAULT_FILTERS,
  filters: DEFAULT_FILTERS,
  results: [],
  numContentCreators: 0,
  isLoading: {
    results: {
      standard: false,
      more: false
    }
  },
  lastStateReset: Date.now(),
  screenState: cloneDeep(initialScreenState)
};

let fetchContentCancelToken;

const handleDeletePosts = (dispatch: Dispatch, results: Array<DiscoveryContentResultImage>): void => {
  const postsWithCodes = results.map(item => ({
    code: item.code,
    assetUrl: item.assetUrl
  }));
  const postsToDelete = getPostsToDelete(postsWithCodes);

  if (postsToDelete.length > 0) {
    dispatch(deletePosts(postsToDelete, 'instagram'));
  }
};

const setLoadingStatus = (type: string, status: boolean): Action => ({
  type,

  payload: {
    status
  }
});

const contentLoaded = (
  content: Array<DiscoveryContentResultImage>,
  numContentCreators: number,
  currentPage: number
): Action => ({
  type: CONTENT_LOADED,

  payload: {
    content,
    numContentCreators,
    currentPage
  }
});

const filtersUpdated = (filters: DiscoveryContentFilters): Action => ({
  type: FILTERS_UPDATED,

  payload: {
    filters
  }
});

const paginationUpdated = (meta: PaginationMeta, links: PaginationLinks): Action => ({
  type: PAGINATION_UPDATED,

  payload: {
    meta,
    links
  }
});

const screenStateUpdated = (updatedScreenState: DiscoveryScreenState): Action => ({
  type: SCREEN_STATE_UPDATED,

  payload: {
    screenState: updatedScreenState
  }
});

export const resetContent = (): Action => ({
  type: CONTENT_RESET
});

export const resetFilters = (clearSearchTerms?: boolean): Action => ({
  type: FILTERS_RESET,

  payload: {
    clearSearchTerms
  }
});

const getSettings = (state: DiscoveryContentState): DiscoveryContentFilterDetails => ({
  screenState: state.screenState,
  sort: state.screenState.sort,
  filters: state.filters
});

const getUpdatedScreenState = (meta: PaginationMeta, links: PaginationLinks, sort: string): DiscoveryScreenState => ({
  page: meta.currentPage || 0,
  sort: sort || DEFAULT_SORT_OPTION,
  totalItems: Number(meta.itemsTotal || 0),
  links,
  meta: { ...meta, itemsTotal: Number(meta.itemsTotal || 0) }
});

const updateFilters = (
  filters: DiscoveryContentFilters,
  updatedFilter: Partial<DiscoveryContentFilters>
): DiscoveryContentFilters => ({
  ...filters,
  ...updatedFilter
});

const updatePagination = (meta: PaginationMeta, links: PaginationLinks): ThunkAction => async (dispatch: Dispatch) => {
  dispatch(paginationUpdated(meta, links));
};

export const updateScreenState = (screenState: DiscoveryScreenState): ThunkAction => async (dispatch: Dispatch) => {
  dispatch(screenStateUpdated(screenState));
};

export const getContent = (options: DiscoveryFilterOptions = { reset: false }): ThunkAction => async (dispatch: Dispatch, getState: GetState) => {
  if (fetchContentCancelToken) {
    fetchContentCancelToken.cancel();
  }

  fetchContentCancelToken = axios.CancelToken.source();

  const loadingAction = isNil(options.reset) ? CONTENT_LOADING_MORE : CONTENT_LOADING;

  const gottenState = getState();
  const state = options.reset ? initialState : gottenState.discoveryContent;
  const settings = getSettings(state);
  const sort = settings.sort;
  const currentPage = options.page || 1;
  const filters = settings.filters;
  const brandProfileId = getBrandProfileId(gottenState.organizations.currentOrganization) || 0;
  const queryObject = buildQueryObjectFromFilters(filters);

  const updatedQueryObject = {
    ...queryObject,
    searchTerms: getSearchTerms(queryObject.searchTerms)
  };

  if (!updatedQueryObject.searchTerms?.length) {
    return null;
  }

  dispatch(setLoadingStatus(loadingAction, true));

  try {
    const url = DISCOVERY.content.getResults(
      brandProfileId,
      currentPage,
      sort,
      options.itemsPerPage ?? ITEMS_PER_PAGE
    );

    const response = await post(url, {
      data: updatedQueryObject,
      cancelToken: fetchContentCancelToken.token
    });

    const results = response.data ? response.data.data : [];
    const numContentCreators = response.data ? response.data.contentCreatorCount : 0;
    const meta = response.data ? response.data.meta : {};
    const links = response.data ? response.data.links : {};
    const updatedScreenState = getUpdatedScreenState(meta, links, sort);

    dispatch(updatePagination(meta, links));
    dispatch(updateScreenState(updatedScreenState));

    const prepared = getPreparedPreloadImages(results);
    const preloadedResults = await Promise.all(prepared);
    const availableResults = preloadedResults.filter(item => Boolean(item.assetUrl));

    handleDeletePosts(dispatch, preloadedResults);
    dispatch(setLoadingStatus(loadingAction, false));

    return dispatch(contentLoaded(availableResults, numContentCreators, currentPage));
  } catch (error) {
    if (axios.isCancel(error)) {
      return; // eslint-disable-line consistent-return
    }

    dispatch(setLoadingStatus(loadingAction, false));

    throw error;
  }
};

export const updateContentFilters = (updatedFilter: Partial<DiscoveryContentFilters>): ThunkAction => async (dispatch: Dispatch, getState: GetState) => { // eslint-disable-line consistent-return
  const settings = getSettings(getState().discoveryContent);

  if (isNil(updatedFilter) || !isObject(updatedFilter)) {
    return null;
  }

  const updatedFilters = updateFilters(settings.filters, updatedFilter);

  dispatch(resetContent());
  dispatch(updateScreenState({
    ...cloneDeep(initialScreenState),
    sort: settings.screenState.sort
  }));
  return dispatch(filtersUpdated(updatedFilters));
};

export const resetContentFilters = (): ThunkAction => (dispatch: Dispatch) => Promise.all([
  dispatch(resetContent()),
  dispatch(resetFilters())
]);

export default createReducer<any, any>('discoveryContent', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [PAGINATION_UPDATED]: (state, { meta, links }) => ({
    ...state,
    screenState: {
      ...state.screenState,
      meta,
      links
    }
  }),
  [SCREEN_STATE_UPDATED]: (state, { screenState }) => ({
    ...state,
    screenState
  }),
  [FILTERS_RESET]: (state, { clearSearchTerms }) => ({
    ...state,
    filters: {
      ...initialState.defaultFilters,
      searchTerms: clearSearchTerms ? [] : state.filters.searchTerms
    },
    lastStateReset: Date.now()
  }),
  [FILTERS_UPDATED]: (state, { filters }) => ({
    ...state,
    filters
  }),
  [CONTENT_RESET]: (state) => ({
    ...state,
    results: [],
    numContentCreators: 0
  }),
  [CONTENT_LOADING]: (state, { status }) => ({
    ...state,
    isLoading: {
      ...state.isLoading,
      results: {
        ...state.isLoading.results,
        standard: status
      }
    }
  }),
  [CONTENT_LOADING_MORE]: (state, { status }) => ({
    ...state,
    isLoading: {
      ...state.isLoading,
      results: {
        ...state.isLoading.results,
        more: status
      }
    }
  }),
  [CONTENT_LOADED]: (state, { content, numContentCreators, currentPage }) => ({
    ...state,
    results: currentPage === 1 ? content : [...state.results, ...content],
    numContentCreators,
    isLoading: {
      ...state.isLoading,
      results: {
        standard: false,
        more: false
      }
    }
  })
});
